import { useQuery } from '@tanstack/react-query';
import { Icon } from '@/components/Icon';
import { fetchCorpusStats } from '@/lib/api';
import { sayTowns } from '@/lib/corpus';
import { formatPrice, monthlyPrice, type CurrencyCode } from '@/lib/pricing';
import { TIERS, type Tier } from '@/lib/tier';
import './Onboarding.css';

/**
 * Onboarding screen 1 — the pre-auth interstitial.
 *
 * Sits between "Get Started" on the marketing site and the Kinde hosted sign-in. Its job is
 * momentum and orientation: the click-to-Kinde jump is the biggest drop-off in the funnel,
 * because the user clicked a marketing promise and is immediately asked for credentials by a
 * domain they do not recognize. One honest screen carries the intent across.
 *
 * **It is a hand-off, not a gate. It never collects credentials** — no email field, no password
 * field, not even a decorative one. All authentication happens at Kinde.
 *
 * The steps must describe the flow that ACTUALLY happens. This screen promised "set your email
 * and password" while every connection on the application is passwordless — an emailed code or
 * a social provider — so the one screen whose job is "no surprises" was the source of the first
 * one. It is also the page a payment provider reads when reviewing the domain.
 *
 * ## The charge amount
 *
 * `BUSINESS_RULES.md` requires this screen to state trial length, that a card is needed, and
 * the exact date AND amount of the first charge. No plan has been chosen at this point, so
 * historically it could only say "from $12/mo".
 *
 * Resolved 08/14/2026: **the marketing CTA passes the plan through.** Someone clicking "Start
 * with Intelligence" on a pricing card arrives with `plan=intelligence`, and this screen names
 * one figure. Only genuinely generic entry falls back to the range. The *date* is always
 * exact either way — it is today plus fourteen days.
 */
export function Interstitial({
  /** Carried from the marketing CTA. Null for a generic "Get Started". */
  plan,
  chargeDate,
  currency = 'USD',
  onCreateAccount,
  onLogIn,
}: {
  plan: Tier | null;
  /** mm/dd/yyyy — always known, since it is today + 14 days. */
  chargeDate: string;
  currency?: CurrencyCode;
  onCreateAccount: () => void;
  onLogIn: () => void;
}) {
  const { data: stats } = useQuery({ queryKey: ['corpus'], queryFn: fetchCorpusStats });
  const towns = stats ? sayTowns(stats) : 'thousands of';

  const cheapest = formatPrice(currency, monthlyPrice(currency, 'snapshot'));
  const exact = plan ? formatPrice(currency, monthlyPrice(currency, plan)) : null;

  return (
    <div className="onboard">
      <div className="onboard__inner">
        <div className="onboard__card">
          <div className="onboard__lead">
            <span className="onboard__badge">
              <Icon name="check" /> 14 days free
            </span>
            {/* Momentum first. The trial terms sit below the steps, not above them. */}
            <h1 className="onboard__h1">You’re two minutes from following your first place.</h1>
            <p className="onboard__lede">
              Aware reads every council, school board and county meeting in {towns} places and writes them
              up in plain English. Here’s exactly what happens next — no surprises.
            </p>
          </div>

          <div className="onboard__body">
            <p className="u-meta onboard__stepsLabel">Three steps, in this order</p>

            <ol className="onboard__steps">
              <li>
                <span className="onboard__dot">1</span>
                <div>
                  <h2>Confirm your email</h2>
                  <p>
                    Takes about thirty seconds. We email you a one-time code — or you can
                    continue with Google or Apple. We use a dedicated sign-in service, so the
                    page will look a little different; that is expected.
                  </p>
                  <p className="onboard__note">
                    <Icon name="lock" /> No password to choose, and none for us to lose
                  </p>
                </div>
              </li>
              <li>
                <span className="onboard__dot">2</span>
                <div>
                  <h2>Pick your place and plan</h2>
                  <p>
                    Choose how many places you want to follow. Your feed fills immediately with
                    meetings already on file — there is nothing to wait for.
                  </p>
                  <p className="onboard__note">
                    <Icon name="pin" /> Change or cancel your plan whenever you like
                  </p>
                </div>
              </li>
              <li>
                <span className="onboard__dot" data-last="true">
                  3
                </span>
                <div>
                  <h2>Read your first recap</h2>
                  <p>
                    Plain-English write-ups of what your council and school board actually
                    decided, with every fact linked to the moment it was said.
                  </p>
                  <p className="onboard__note">
                    <Icon name="zap" /> Meetings are processed within hours of going up
                  </p>
                </div>
              </li>
            </ol>

            {/* Trial terms: below the steps, and stating all three required facts. */}
            <div className="onboard__terms">
              <Icon name="calendar" className="onboard__termsIcon" />
              <div>
                <p className="onboard__termsLead">Free until {chargeDate}. Nothing today.</p>
                <p className="onboard__termsBody">
                  A card is needed to start, and your first payment isn’t taken until{' '}
                  {chargeDate} —{' '}
                  {exact ? (
                    <>
                      <strong>
                        {exact} a month for {TIERS[plan!].label}
                      </strong>
                    </>
                  ) : (
                    <>from {cheapest} a month depending on the plan you pick</>
                  )}
                  . Cancel before then in two clicks and you’re never charged.
                </p>
              </div>
            </div>

            <button type="button" className="onboard__cta" onClick={onCreateAccount}>
              Create my account <Icon name="arrow-right" />
            </button>

            <p className="onboard__handoff">
              <Icon name="lock" /> The next screen confirms your email. It’s our sign-in
              partner, so the address bar changes.
            </p>
          </div>
        </div>

        <p className="onboard__login">
          Already have an account?{' '}
          <button type="button" onClick={onLogIn}>
            Log in
          </button>
        </p>
      </div>
    </div>
  );
}
